<template>
  <div class="search-form">
    <div class="methods">
      <button type="button" class="method method--wandh" :disabled="disableWandh" @click="handleClickWandh">
        <Icon name="ai" size="2md" color="gray-400" class="search-form__icon" />
        <span class="method__label">AIに質問する</span>
      </button>
      <button type="button" class="method method--keyword" @click="handleClickKeyword">
        <Icon name="keyword" size="2md" color="gray-400" class="search-form__icon" />
        <span class="method__label">キーワードで探す</span>
      </button>
    </div>
    <div class="input">
      <div class="wandh-form">
        <textarea
          ref="wandhForm"
          v-model="wandhQuestion"
          :placeholder="wandhPlaceholder"
          rows="3"
          :style="wandhFormStyle"
          @input="handleWandhInput"
          @keydown.enter="handleWandhEnter"
        />
      </div>
      <div class="keyword-form">
        <input
          ref="keywordForm"
          :value="inputSearchKeyword"
          tabindex="-1"
          type="text"
          placeholder="キーワード"
          class="keyword-form__textbox"
          @input="handleKeywordInput"
          @keydown.enter="handleKeywordEnter"
        />
        <div tabindex="-1" class="keyword-form__textbox keyword-form__textbox--readonly" @click="handleClickKeyword">
          {{ searchConditions }}
        </div>
      </div>
    </div>
    <div class="submits">
      <div class="filter">
        <button type="button" tabindex="-1" @click="handleClickFilterOpen">
          <span>詳細</span>
          <Icon name="filter-open" size="md" color="blue-400" />
        </button>
      </div>
      <button type="button" :disabled="!isKeywordAskable" class="submit" tabindex="-1" @click="submit">
        <Icon name="search" size="sm" color="gray-0" />
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import Icon from '@/components/renewal/common/icon.vue';
import { useStore } from '@/store/useStore';
import { useAuth } from '@/plugins/auth';
import { QuestionBytesLimit, WandhPlaceholder } from '@/constants';
import { handleWandhEnterKey, handleKeywordEnterKey } from '@/utils/keyboardUtils';
import { isMobile } from '@/utils/deviceUtils';

export interface SearchFormProps {
  inputSearchKeyword: string;
  isKeywordAskable: boolean;
  searchConditions: string;
}

export type SearchFormEmits = {
  (e: 'update:inputSearchKeyword', value: string): void;
  (e: 'changeType', type: 'wandh' | 'keyword'): void;
  (e: 'changeShowFilter', show: boolean): void;
  (e: 'submit'): void;
};

defineProps<SearchFormProps>();
const emit = defineEmits<SearchFormEmits>();

const auth = useAuth();
const store = useStore();

const wandhForm = ref<HTMLTextAreaElement | null>(null);
const keywordForm = ref<HTMLInputElement | null>(null);

// 可変行数機能のためのスタイル
const wandhFormStyle = ref({ height: 'auto' });

const wandhPlaceholder = ref<string>(WandhPlaceholder.Desktop);

onMounted(() => {
  wandhPlaceholder.value = isMobile() ? WandhPlaceholder.Mobile : WandhPlaceholder.Desktop;
});

// Watson & Holmes を利用できない
const disableWandh = computed<boolean>(() => {
  return !(auth.permissions.labsQA || auth.permissions.labsQAWatson);
});

const wandhQuestion = computed({
  get: () => store.state.persistent.labs.wandh.inputQuery.question,
  set: (newVal) => {
    store.commit('setLabsWandhInputQuery', { ...store.state.persistent.labs.wandh.inputQuery, question: newVal });
  },
});

const wandhQuestionBytes = computed(() => new TextEncoder().encode(wandhQuestion.value.trim()).length);

const handleWandhInput = async (event: Event) => {
  const target = event.target as HTMLTextAreaElement;

  // scrollHeightを正しく取得するために一度height: auto;にする
  wandhFormStyle.value.height = 'auto';

  // height: auto;が反映されるまで待つ
  await nextTick();

  wandhFormStyle.value.height = `${target.scrollHeight}px`;
};

const handleClickWandh = () => {
  emit('changeType', 'wandh');
  wandhForm.value?.focus();
};

const handleClickKeyword = async () => {
  emit('changeType', 'keyword');

  // NOTE: 検索条件(searchConditions)が表示されているときにクリックされるとフォーカスされないことがあるためnextTickで少し待機する
  await nextTick();

  keywordForm.value?.focus();
};

const handleClickFilterOpen = () => {
  emit('changeShowFilter', true);
};

const handleWandhEnter = (event: KeyboardEvent) => {
  handleWandhEnterKey(event, () => emit('submit'));
};

const handleKeywordEnter = (event: KeyboardEvent) => {
  handleKeywordEnterKey(event, () => emit('submit'));
};

const submit = () => {
  emit('submit');
};

const handleKeywordInput = (event: Event) => {
  emit('update:inputSearchKeyword', (event.target as HTMLInputElement).value);
};

const isWandhQuestionValid = computed(() => {
  return (
    // 質問が入力されている
    wandhQuestionBytes.value > 0 &&
    // 質問が文字数制限内
    wandhQuestionBytes.value <= QuestionBytesLimit
    // 期間指定がされている。未入力は undefined, 空にすると '' になる
  );
});

defineExpose({
  isWandhQuestionValid,
});
</script>

<style lang="scss" scoped src="./search-form.scss" />
